import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Collapse, Stack, Typography } from "@mui/material"
import { ReactElement, useState } from "react"

import { AppError, parseErrorAsObj } from "../../errors/errorProcessors"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import AppAlert from "../dataDisplay/alerts/appAlert"
import VerticalKeyValueList from "../dataDisplay/verticalKeyValueList"
import AppPaper from "../surfaces/appPaper"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

interface IErrorDisplayProps {
    error: AppError
}

/**
 * Displays an app error to a user in a consistent, readable format. Takes
 * AppError as input, extracts required information and displays it to the user.
 */
const ErrorDisplay = ({ error }: IErrorDisplayProps): ReactElement => {
    const errorObj = parseErrorAsObj(error)
    const [showTechnicalDetails, setShowTechnicalDetails] = useState(false)

    return (
        <div data-testid="error-display">
            <AppPaper>
                <Stack spacing={GRID_SPACING}>
                    <div>
                        <Typography variant="h5" fontWeight={500}>
                            {errorObj.message}
                        </Typography>
                        <Typography variant="h6" fontWeight={400}>
                            {errorObj.displayDetail}
                        </Typography>
                    </div>
                    <Stack spacing={GRID_SPACING} direction="row">
                        <AppButton
                            onClick={() =>
                                setShowTechnicalDetails((prev) => !prev)
                            }
                            variant="outlined"
                            sx={{ width: "250px" }}
                            endIcon={
                                showTechnicalDetails ? (
                                    <ExpandLessIcon />
                                ) : (
                                    <ExpandMoreIcon />
                                )
                            }
                        >
                            {showTechnicalDetails ? "Hide" : "Show"} Technical
                            Details
                        </AppButton>
                        <AppButton
                            color="warning"
                            variant="outlined"
                            onClick={() =>
                                window.open(CONFIG.SN_PORTAL_URL, "_blank")
                            }
                        >
                            Go to Support Portal
                        </AppButton>
                    </Stack>

                    {/* Create a bullet point list */}
                    <Collapse in={showTechnicalDetails}>
                        <AppAlert severity="warning">
                            <Typography
                                variant="body1"
                                fontWeight={500}
                                marginBottom={1}
                            >
                                The following technical details were collected
                                from this issue. If raising a support ticket,
                                please include these details.
                            </Typography>
                            <VerticalKeyValueList
                                kwargs={{
                                    "Internal Error Code": errorObj.code,
                                    "Error Level": errorObj.level,
                                    "Technical Details on Error":
                                        errorObj.technicalDetail,
                                }}
                            />
                        </AppAlert>
                    </Collapse>
                </Stack>
            </AppPaper>
        </div>
    )
}

export default ErrorDisplay
